import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useCallback, useEffect, useRef } from 'react'
import { isOfficeInitialized } from '../helpers/outlookHelper'

export type OfficeEventHandlerSignature = {
    id: string
    eventType: Office.EventType
    handle: (event: any) => void
    complete?: (result: Office.AsyncResult<any>) => void
}

type OfficeEventHandlerType = {
    addHandler: (handler: OfficeEventHandlerSignature) => void
    removeHandler: (id: string) => void
}

export type OfficeDragAndDropEventArgs = {
    dragAndDropEventData: {
        type: string
        dataTransfer: {
            files: {
                fileContent: Blob
                name: string
                type: string
            }[]
        }
    }
}

const OfficeEventHandlerContext = createContext<OfficeEventHandlerType>({ addHandler: undefined, removeHandler: undefined })

export function OfficeEventHandlerProvider(props: PropsWithChildren): ReactElement {
    const callbackHandlers = useRef<OfficeEventHandlerSignature[]>([])
    const setCallbackHandlers = (items: OfficeEventHandlerSignature[]) => {
        callbackHandlers.current = items
    }

    const handleHandlersByEventType = useCallback(
        (eventType: Office.EventType, event: any): void => {
            callbackHandlers.current.filter(handler => handler.eventType === eventType)?.forEach(callback => callback.handle(event))
        },
        [callbackHandlers],
    )
    const completeHandlersByEventType = useCallback(
        (eventType: Office.EventType, result: Office.AsyncResult<any>): void => {
            callbackHandlers.current.filter(handler => handler.eventType === eventType)?.forEach(callback => callback.complete?.(result))
        },
        [callbackHandlers],
    )

    useEffect(() => {
        if (isOfficeInitialized() && Office.context.platform === Office.PlatformType.OfficeOnline) {
            Office.context.mailbox.addHandlerAsync(
                Office.EventType.DragAndDropEvent,
                (event: OfficeDragAndDropEventArgs) => handleHandlersByEventType(Office.EventType.DragAndDropEvent, event),
                (result: Office.AsyncResult<any>) => completeHandlersByEventType(Office.EventType.DragAndDropEvent, result),
            )
        }
    }, [])

    return (
        <OfficeEventHandlerContext.Provider
            value={{
                addHandler: (handler: OfficeEventHandlerSignature) => {
                    setCallbackHandlers([...callbackHandlers.current, handler])
                },
                removeHandler: (id: string) => {
                    setCallbackHandlers([...callbackHandlers.current.filter(handler => handler.id !== id)])
                },
            }}
        >
            {props.children}
        </OfficeEventHandlerContext.Provider>
    )
}

// noinspection JSUnusedGlobalSymbols
export default OfficeEventHandlerContext
